import { Button } from "@/components/ui/button";
import { 
  ArrowRight, 
  Check, 
  UserPlus, 
  Image, 
  Link2, 
  Search, 
  Briefcase,
  BarChart3,
  QrCode,
  Smartphone,
  Users,
  Palette,
  Share2,
  Star,
  Zap,
  Globe,
  Building2,
  Mic,
  Camera,
  Heart
} from "lucide-react";

const HomepageNewFreeQCard = () => {
  return (
    <div className="min-h-screen bg-background">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center overflow-hidden bg-gradient-to-br from-slate-50 via-orange-50/30 to-slate-100">
        {/* Background elements */}
        <div className="absolute top-20 right-20 w-96 h-96 bg-primary/10 rounded-full blur-3xl" />
        <div className="absolute bottom-20 left-20 w-80 h-80 bg-orange-200/20 rounded-full blur-3xl" />
        
        <div className="container mx-auto px-4 py-20 relative z-10">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Left: Content */}
            <div className="space-y-8">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 text-primary font-semibold text-sm border border-primary/20">
                <Zap className="w-4 h-4" />
                Now Free for Everyone
              </div>
              
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-foreground leading-tight">
                The Professional
                <span className="block text-primary">Engagement Platform</span>
              </h1>
              
              <p className="text-xl text-muted-foreground leading-relaxed max-w-xl">
                Create your digital identity. Share your story. Get saved into contacts instantly. 
                <span className="font-semibold text-foreground"> Now completely free.</span>
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <Button size="lg" className="bg-primary hover:bg-primary/90 text-primary-foreground text-lg px-8 py-6 font-semibold shadow-lg shadow-primary/30">
                  Create Your Free QCard
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
                <Button size="lg" variant="outline" className="text-lg px-8 py-6 border-2">
                  See How It Works
                </Button>
              </div>
              
              <div className="flex items-center gap-6 text-sm text-muted-foreground">
                <div className="flex items-center gap-2">
                  <Check className="w-5 h-5 text-green-500" />
                  100% Free
                </div>
                <div className="flex items-center gap-2">
                  <Check className="w-5 h-5 text-green-500" />
                  No credit card
                </div>
                <div className="flex items-center gap-2">
                  <Check className="w-5 h-5 text-green-500" />
                  Ready in 60 seconds
                </div>
              </div>
            </div>
            
            {/* Right: Phone mockup */}
            <div className="relative flex justify-center lg:justify-end">
              <div className="relative">
                {/* Phone frame */}
                <div className="relative w-72 h-[580px] bg-slate-900 rounded-[3rem] p-3 shadow-2xl shadow-slate-900/30">
                  <div className="absolute top-6 left-1/2 -translate-x-1/2 w-20 h-6 bg-slate-900 rounded-full z-20" />
                  <div className="w-full h-full bg-white rounded-[2.5rem] overflow-hidden">
                    {/* Profile header */}
                    <div className="h-32 bg-gradient-to-br from-primary to-orange-400 relative">
                      <div className="absolute -bottom-12 left-1/2 -translate-x-1/2">
                        <div className="w-24 h-24 rounded-full bg-white p-1 shadow-lg">
                          <div className="w-full h-full rounded-full bg-gradient-to-br from-slate-200 to-slate-300 flex items-center justify-center">
                            <span className="text-2xl font-bold text-slate-600">JD</span>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    {/* Profile content */}
                    <div className="pt-16 px-6 pb-6 text-center">
                      <h3 className="font-bold text-lg text-slate-900">Jane Doe</h3>
                      <p className="text-sm text-slate-500">Marketing Director</p>
                      <p className="text-xs text-slate-400 mt-1">Acme Corporation</p>
                      
                      <Button className="w-full mt-4 bg-primary hover:bg-primary/90 text-white font-semibold">
                        <UserPlus className="w-4 h-4 mr-2" />
                        Add to Contacts
                      </Button>
                      
                      <div className="mt-4 grid grid-cols-3 gap-2">
                        {["Call", "Email", "Website"].map((action) => (
                          <div key={action} className="py-2 px-3 bg-slate-100 rounded-lg text-xs font-medium text-slate-600">
                            {action}
                          </div>
                        ))}
                      </div>
                      
                      <div className="mt-4 flex justify-center gap-3">
                        {[1, 2, 3, 4].map((i) => (
                          <div key={i} className="w-8 h-8 bg-slate-100 rounded-full" />
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Floating badge */}
                <div className="absolute -top-4 -right-4 bg-green-500 text-white px-4 py-2 rounded-full font-bold text-sm shadow-lg">
                  FREE
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Problem → Solution Section */}
      <section className="py-24 bg-slate-900 text-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              The Problem With Professional Networking
            </h2>
            <p className="text-xl text-slate-400 max-w-2xl mx-auto">
              Great connections are made every day — but most of them vanish before they become opportunities.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {/* Problem */}
            <div className="bg-slate-800/50 rounded-2xl p-6 border border-slate-700">
              <div className="w-12 h-12 rounded-xl bg-red-500/20 flex items-center justify-center mb-4">
                <span className="text-2xl">😔</span>
              </div>
              <h3 className="text-lg font-bold text-red-400 mb-2">The Problem</h3>
              <p className="text-slate-400">
                You meet great people, but they forget your name, face, and what you do within days.
              </p>
            </div>
            
            {/* Impact */}
            <div className="bg-slate-800/50 rounded-2xl p-6 border border-slate-700">
              <div className="w-12 h-12 rounded-xl bg-orange-500/20 flex items-center justify-center mb-4">
                <span className="text-2xl">📉</span>
              </div>
              <h3 className="text-lg font-bold text-orange-400 mb-2">The Impact</h3>
              <p className="text-slate-400">
                Missed referrals. Lost deals. Opportunities that go to someone who was simply remembered.
              </p>
            </div>
            
            {/* Solution */}
            <div className="bg-slate-800/50 rounded-2xl p-6 border border-primary/30">
              <div className="w-12 h-12 rounded-xl bg-primary/20 flex items-center justify-center mb-4">
                <Smartphone className="w-6 h-6 text-primary" />
              </div>
              <h3 className="text-lg font-bold text-primary mb-2">QCard Solution</h3>
              <p className="text-slate-400">
                One tap instantly saves you into their contacts — with your photo, links, and keywords.
              </p>
            </div>
            
            {/* Benefit */}
            <div className="bg-slate-800/50 rounded-2xl p-6 border border-green-500/30">
              <div className="w-12 h-12 rounded-xl bg-green-500/20 flex items-center justify-center mb-4">
                <span className="text-2xl">🎯</span>
              </div>
              <h3 className="text-lg font-bold text-green-400 mb-2">The Benefit</h3>
              <p className="text-slate-400">
                You're always findable, always top-of-mind, and always the first call when opportunity knocks.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Add to Contacts Section */}
      <section className="py-24 bg-gradient-to-b from-white to-slate-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 text-primary font-semibold text-sm mb-4">
              <UserPlus className="w-4 h-4" />
              The QCard Advantage
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
              Instantly Saved. Never Forgotten.
            </h2>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Unlike paper cards or basic digital cards, QCard adds you directly to their phone's Contacts app.
            </p>
          </div>
          
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Left: What gets saved */}
            <div className="space-y-6">
              <h3 className="text-2xl font-bold text-foreground">What Gets Saved to Their Phone:</h3>
              
              {[
                { icon: Image, title: "Your Photo", desc: "They'll recognize your face, not just your name" },
                { icon: Briefcase, title: "Name & Title", desc: "Your full professional identity at a glance" },
                { icon: Link2, title: "All Your Links", desc: "Website, LinkedIn, portfolio — all clickable" },
                { icon: Search, title: "Searchable Keywords", desc: "They can find you by 'marketing' or 'NYC' later" },
              ].map((item, i) => (
                <div key={i} className="flex items-start gap-4 bg-white rounded-xl p-5 shadow-sm border border-slate-100">
                  <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <item.icon className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-foreground">{item.title}</h4>
                    <p className="text-muted-foreground">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
            
            {/* Right: Comparison */}
            <div className="space-y-6">
              <div className="bg-slate-100 rounded-2xl p-6 border-2 border-slate-200">
                <h4 className="font-bold text-slate-500 mb-4 flex items-center gap-2">
                  <span className="text-2xl">📇</span> Paper Business Cards
                </h4>
                <ul className="space-y-3 text-slate-600">
                  {[
                    "Lost or thrown away within 48 hours",
                    "No photo to remember your face",
                    "Can't be searched or found later",
                    "No links or social profiles",
                    "Static — can't be updated"
                  ].map((item, i) => (
                    <li key={i} className="flex items-center gap-2">
                      <span className="text-red-500">✗</span> {item}
                    </li>
                  ))}
                </ul>
              </div>
              
              <div className="bg-primary/5 rounded-2xl p-6 border-2 border-primary/30">
                <h4 className="font-bold text-primary mb-4 flex items-center gap-2">
                  <Smartphone className="w-6 h-6" /> QCard
                </h4>
                <ul className="space-y-3 text-foreground">
                  {[
                    "Saved directly to their Contacts app",
                    "Your photo front and center",
                    "Searchable by name, title, or keywords",
                    "All links included and clickable",
                    "Always up-to-date automatically"
                  ].map((item, i) => (
                    <li key={i} className="flex items-center gap-2">
                      <Check className="w-5 h-5 text-green-500" /> {item}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Free Tier Section */}
      <section className="py-24 bg-slate-900 text-white relative overflow-hidden">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-green-500/10 rounded-full blur-[120px]" />
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-green-500/20 text-green-400 font-semibold text-sm border border-green-500/30 mb-4">
              <Star className="w-4 h-4" />
              New: Free Forever
            </div>
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Your Free QCard Includes <span className="text-green-400">Everything</span>
            </h2>
            <p className="text-xl text-slate-400 max-w-2xl mx-auto">
              No hidden fees. No "premium" features locked away. Just a powerful professional identity — free.
            </p>
          </div>
          
          <div className="max-w-4xl mx-auto">
            <div className="bg-slate-800/50 backdrop-blur-sm rounded-3xl border border-slate-700 overflow-hidden">
              <div className="bg-gradient-to-r from-green-600 to-green-500 px-8 py-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-green-100 text-sm mb-1">QCard Free</p>
                    <div className="flex items-baseline gap-2">
                      <span className="text-5xl font-bold text-white">$0</span>
                      <span className="text-green-100">/forever</span>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-green-200/60 text-sm line-through">$12/month elsewhere</p>
                    <p className="text-white font-semibold">Save $144/year</p>
                  </div>
                </div>
              </div>
              
              <div className="p-8">
                <div className="grid sm:grid-cols-2 gap-4 mb-8">
                  {[
                    "Your personalized QCard profile",
                    "Photo, name, title & company",
                    "Phone, email & website links",
                    "All social media profiles",
                    "Instant 'Add to Contacts' button",
                    "Shareable QR code",
                    "Personal link (qcard.me/you)",
                    "Unlimited sharing"
                  ].map((feature, i) => (
                    <div key={i} className="flex items-center gap-3">
                      <div className="w-6 h-6 rounded-full bg-green-500/20 flex items-center justify-center flex-shrink-0">
                        <Check className="w-4 h-4 text-green-400" />
                      </div>
                      <span className="text-white/80">{feature}</span>
                    </div>
                  ))}
                </div>
                
                <div className="text-center">
                  <Button size="lg" className="bg-primary hover:bg-primary/90 text-primary-foreground text-lg px-12 py-6 font-semibold shadow-lg shadow-primary/30">
                    Create Your Free QCard
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                  <p className="mt-4 text-sm text-slate-500">
                    No credit card required. Ready in 60 seconds.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Platform Features Section */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
              More Than a Digital Card — A <span className="text-primary">Professional Platform</span>
            </h2>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              QCard is your complete professional identity hub, designed for the modern networker.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              { icon: Palette, title: "Customizable Profile", desc: "Design your profile to match your personal brand with colors, layouts, and sections." },
              { icon: Link2, title: "Smart Links", desc: "Add unlimited links to your website, portfolio, booking page, or any URL." },
              { icon: BarChart3, title: "Analytics Dashboard", desc: "See who viewed your profile, when, and which links they clicked." },
              { icon: Users, title: "Team & Enterprise", desc: "Manage your entire organization's digital identities from one dashboard." },
              { icon: QrCode, title: "QR Code & NFC", desc: "Share your QCard with a quick scan or tap — works on any device." },
              { icon: Globe, title: "Modern Digital Identity", desc: "One link for everything: bio, work, social, and contact info." },
            ].map((feature, i) => (
              <div key={i} className="bg-slate-50 rounded-2xl p-6 border border-slate-100 hover:border-primary/30 hover:shadow-lg transition-all duration-300">
                <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center mb-4">
                  <feature.icon className="w-7 h-7 text-primary" />
                </div>
                <h3 className="text-xl font-bold text-foreground mb-2">{feature.title}</h3>
                <p className="text-muted-foreground">{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Use Cases / Social Proof Section */}
      <section className="py-24 bg-slate-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
              Trusted by Professionals Everywhere
            </h2>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              From entrepreneurs to executives, creatives to consultants — QCard helps professionals stand out.
            </p>
          </div>
          
          {/* Testimonials */}
          <div className="grid md:grid-cols-3 gap-8 mb-16">
            {[
              { name: "Sarah Chen", role: "Real Estate Agent", quote: "I've closed 3 deals this quarter from people who saved my QCard at open houses. They found me months later!", avatar: "SC" },
              { name: "Marcus Johnson", role: "Startup Founder", quote: "At conferences, I tap phones instead of handing out cards. My follow-up rate went from 10% to over 60%.", avatar: "MJ" },
              { name: "Emily Rodriguez", role: "Marketing Consultant", quote: "Clients can actually find me when they need me. No more 'sorry I lost your card' excuses.", avatar: "ER" },
            ].map((testimonial, i) => (
              <div key={i} className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100">
                <div className="flex gap-1 mb-4">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <Star key={star} className="w-5 h-5 fill-primary text-primary" />
                  ))}
                </div>
                <p className="text-foreground mb-6 italic">"{testimonial.quote}"</p>
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                    <span className="font-bold text-primary">{testimonial.avatar}</span>
                  </div>
                  <div>
                    <p className="font-semibold text-foreground">{testimonial.name}</p>
                    <p className="text-sm text-muted-foreground">{testimonial.role}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
          
          {/* Industries */}
          <div className="text-center">
            <p className="text-sm text-muted-foreground mb-6">USED BY PROFESSIONALS IN</p>
            <div className="flex flex-wrap justify-center gap-4">
              {[
                { icon: Building2, label: "Real Estate" },
                { icon: Briefcase, label: "Consulting" },
                { icon: Camera, label: "Photography" },
                { icon: Mic, label: "Speaking" },
                { icon: Heart, label: "Healthcare" },
                { icon: Share2, label: "Marketing" },
              ].map((industry, i) => (
                <div key={i} className="flex items-center gap-2 px-4 py-2 bg-white rounded-full border border-slate-200">
                  <industry.icon className="w-4 h-4 text-muted-foreground" />
                  <span className="text-sm font-medium text-muted-foreground">{industry.label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-24 bg-gradient-to-br from-primary to-orange-500 text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmZmZmYiIGZpbGwtb3BhY2l0eT0iMC4xIj48Y2lyY2xlIGN4PSIzMCIgY3k9IjMwIiByPSIyIi8+PC9nPjwvZz48L3N2Zz4=')] opacity-30" />
        
        <div className="container mx-auto px-4 relative z-10 text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Start Free. Be Remembered.
          </h2>
          <p className="text-xl text-white/80 max-w-2xl mx-auto mb-8">
            Join thousands of professionals who refuse to be forgotten. Create your QCard in 60 seconds — completely free.
          </p>
          
          <Button size="lg" className="bg-white hover:bg-white/90 text-primary text-lg px-12 py-6 font-bold shadow-xl">
            Create Your Free QCard
            <ArrowRight className="ml-2 h-5 w-5" />
          </Button>
          
          <div className="mt-8 flex items-center justify-center gap-8 text-sm text-white/70">
            <div className="flex items-center gap-2">
              <Check className="w-5 h-5" />
              Free forever
            </div>
            <div className="flex items-center gap-2">
              <Check className="w-5 h-5" />
              No credit card
            </div>
            <div className="flex items-center gap-2">
              <Check className="w-5 h-5" />
              60-second setup
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-12">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="text-2xl font-bold text-primary">QCard</div>
            <div className="flex gap-6 text-sm text-slate-400">
              <a href="#" className="hover:text-white transition-colors">Privacy</a>
              <a href="#" className="hover:text-white transition-colors">Terms</a>
              <a href="#" className="hover:text-white transition-colors">Contact</a>
            </div>
            <p className="text-sm text-slate-500">© 2024 QCard. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default HomepageNewFreeQCard;
